import React, { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Stack,
  Tooltip,
  Link,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Chip,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import backLogo from '../../../asset/images/grey-circuit-bg.jpg';
import goldCertifiedBadge from '../../../asset/images/Designer.png';
import amazonLogo from '../../../asset/images/AmazonAgentBanner.png';
import googleLogo from '../../../asset/images/GoogleAgentBanner.png';
import microsoftLogo from '../../../asset/images/MicrosoftAgentBanner.png';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import servicenowLogo from '../../../asset/images/Servicenow.jpeg';
import {
  useDownloadAgentMutation,
  useGetAgentsListQuery,
  usePutAgentDemoVideoMutation,
  useGetGoldCertificateListQuery,
  useTrackAgentClickMutation,
} from '../../../data/api/endPointsSlice';
import { ApiError, downloadAgent, showSnackbarErrorMessage, agentFinderChatBotId } from '../../../shared/utiles/Utiles';
import CategoryIcon from '@mui/icons-material/Category';
import baseUrl from '../../../data/json/baseURL.json';
import useAgentFiltering from '../../../shared/customHooks/useAgentFiltering/UseAgentFiltering';
import './AgentMarketPlace.css';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/redux-hooks';
import { resetAgentsFilters, setAgentsFiletrs } from '../../../reduxStore/slices/AgentsFiltersSlice';
import { LogButtonEventToGA, LogTabEventToGA } from '../../../shared/googleAnalytics/GoogleAnalytics';
import {
  agentDownloadClickedText,
  agentSearchPerformedText,
  agentSendClickedText,
  clearFiltersClickedText,
  moreDetailsLinkClickedText,
} from '../../../shared/googleAnalytics/GoogleAnalyticsConstants';
import AgentReviewAnalysisDialog from './AgentReviewAnalysisDialog';
import AgentDemoVideoDialog from './AgentDemoVideoDialog';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AgentCardsFilter from './AgentCardsFilter';
import { useAgentStartStopHandle } from './AgentStartStopHandle';
import { enqueueSnackbar } from 'notistack';
import AgentFinderChatBot from '../../../shared/agentFinderChatBot/AgentFinderChatBot';
export interface AgentMarketPlaceItemWithFilter {
  _id: string;
  party_type: string;
  name: string;
  description: string;
  service_name: string;
  port: number;
  allow_all_access: boolean;
  owners: { name: string; email: string }[];
  users: string[];
  last_updated: string;
  icon: string;
  category: string;
  role: string;
  isPlatformAgent?: boolean;
  datasetId?: string;
  supports_file_upload?: boolean;
  file_format_supported: string[];
  prompt1: string;
  prompt2: string;
  prompt3: string;
  tech_stack: string[];
  cloud_provider: string;
  industry: string;
  isLive?: boolean;
  custom_ui?: string;
  downloadable_only?: boolean;
  supports_dataset?: string;
  datasetIds?: Record<string, string>;
  ui_url?: string;
  ratings_count: number;
  average_rating: number;
  detailed_description?: string;
  usage_instruction?: string;
  use_case?: string;
  download_count: number;
  demo_video_url: string | null;
  download_url?: string;
  folder_name?:string;
  agents?: number;
  more_details_count?: number;
  start_agent_count?: number;
  open_chat_count?: number;
}

export enum AgentCardsFilterType {
  Category = 'category',
  Role = 'role',
  CloudProvider = 'cloudProvider',
}

export enum SortAgentsField {
  Cloud_Provider = 'cloudProvider',
  Date = 'date',
  Category = 'category',
  Certified_Agent = 'certifiedAgent',
  Download = 'download',
  Name = 'name',
  Owner = 'owner',
  Rating = 'rating',
  Role = 'role',
}

export enum SortAgentsFieldsOrder {
  Ascending = 'ascending',
  Descending = 'descending',
}

interface AgentMarketPlaceProps {
  setShowChat: (show: boolean) => void;
  setAgentDetails: (details: AgentMarketPlaceItemWithFilter | null) => void;
  setIsUploadRequired: (isUploadRequired: boolean) => void;
  setfile_format_supported: (file_format_supported: string[]) => void;
  setShowCommingSoonChat: (show: boolean) => void; // Optional prop for CommingSoonChat
}

export interface AgentStatusItem {
  _id: string;
  status: string;
}

export interface startAgentResponse {
  message: string;
  workflow_run_id: number;
  status_code: number;
}

export interface AgentStatusList {
  statuses: AgentStatusItem[];
  total_count: number;
}

function AgentMarketPlace({
  setShowChat,
  setAgentDetails,
  setIsUploadRequired,
  setfile_format_supported,
  setShowCommingSoonChat,
}: AgentMarketPlaceProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortAgentsField, setSortAgentsField] = useState<SortAgentsField | ''>('');
  const [sortAgentFieldsOrder, setSortAgentFieldsOrder] = useState<SortAgentsFieldsOrder>(
    SortAgentsFieldsOrder.Ascending
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedAgentForReview, setSelectedAgentForReview] = useState<AgentMarketPlaceItemWithFilter | null>(null);
  const [selectedAgentForDemo, setSelectedAgentForDemo] = useState<AgentMarketPlaceItemWithFilter | null>(null);
  const {
    data: agentList = [],
    isLoading: agentListLoading,
    error: agentListError,
    refetch: refetchAgentList,
  } = useGetAgentsListQuery();

  // Gold certificate list for all agents
  // Extract goldList from certificates array in API response
  const { data: goldCertResponse } = useGetGoldCertificateListQuery();

  const goldList = useMemo(() => {
    if (Array.isArray(goldCertResponse)) {
      return goldCertResponse;
    } else if (
      goldCertResponse &&
      typeof goldCertResponse === 'object' &&
      'certificates' in goldCertResponse &&
      Array.isArray((goldCertResponse as any).certificates)
    ) {
      return (goldCertResponse as any).certificates;
    }
    return [];
  }, [goldCertResponse]);

  const { handleAgentAction, getAgentsStatus, setAgentStartedAlready, skipAllAgentsStatusCheck } =
    useAgentStartStopHandle();
  const [downloadAgentMutation] = useDownloadAgentMutation();
  const [putAgentDemoVideoMutation] = usePutAgentDemoVideoMutation();
  const [trackAgentClick] = useTrackAgentClickMutation();
  const { accounts } = useMsal();
  const selectedFilters = useAppSelector((state) => state.agentsFilters.agentsFilters);
  const showOnlyGoldRating = selectedFilters.showOnlyGoldRating;
  const dispatch = useAppDispatch();

  // Add pagination state
  const [page, setPage] = useState(1);
  const agentsCardsPerPage = 15;

  // Filter out the agent finder chatbot from the agent list
  const filteredAgentList = useMemo(() => {
    return agentList.filter((agent) => agent._id !== agentFinderChatBotId);
  }, [agentList]);

  // Extract gold agent IDs for filtering
  const goldAgentIds = useMemo(() => {
    if (goldList && Array.isArray(goldList)) {
      return goldList
        .filter((item) => item.gold_rating)
        .map((item) => item.agent_id);
    }
    return [];
  }, [goldList]);

  const {
    categoryList,
    rolesList,
    cloudProviderList,
    filteredAgents,
    totalPageCount: totalPageCountOfAgents,
    paginatedAgents,
    isAgentDownloadable,
    isAgentChattable,
  } = useAgentFiltering(
    filteredAgentList,
    searchTerm,
    selectedFilters.category,
    selectedFilters.role,
    selectedFilters.cloudProvider,
    page,
    agentsCardsPerPage,
    sortAgentsField,
    sortAgentFieldsOrder,
    goldAgentIds,
    showOnlyGoldRating
  );

  const agentCardsFilterSections = [
    {
      title: 'Cloud Provider',
      id: AgentCardsFilterType.CloudProvider,
      items: cloudProviderList,
      selectedFilters: selectedFilters.cloudProvider,
      setSelectedFilters: (filters: string[]) => dispatch(setAgentsFiletrs({ cloudProvider: filters })),
    },
    {
      title: 'Domain / Industry',
      id: AgentCardsFilterType.Category,
      items: categoryList,
      selectedFilters: selectedFilters.category,
      setSelectedFilters: (filters: string[]) => dispatch(setAgentsFiletrs({ category: filters })),
    },
    {
      title: 'Role',
      id: AgentCardsFilterType.Role,
      items: rolesList,
      selectedFilters: selectedFilters.role,
      setSelectedFilters: (filters: string[]) => dispatch(setAgentsFiletrs({ role: filters })),
    },
  ];

  useEffect(() => {
    if (agentListError) {
      showSnackbarErrorMessage(agentListError as ApiError);
    }
    refetchAgentList();
  }, [agentListError, refetchAgentList]);

  // Sort handlers
  const handleAgentsSortFieldChange = (event: SelectChangeEvent<string>) => {
    setSortAgentsField(event.target.value as SortAgentsField | '');
    setPage(1);
  };

  const handleToggleAgentsSortOrder = () => {
    setSortAgentFieldsOrder(
      sortAgentFieldsOrder === SortAgentsFieldsOrder.Ascending
        ? SortAgentsFieldsOrder.Descending
        : SortAgentsFieldsOrder.Ascending
    );
    setPage(1);
  };

  const handleGoldRatingCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setAgentsFiletrs({ showOnlyGoldRating: event.target.checked }));
    setPage(1);
  };

  // Reset to first page when filters or search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedFilters]);

  // Handle page change
  const handleAgentsPageChange = (newPage: number) => {
    setPage(newPage);
    // Scroll to top of the agent section when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activateAgent = (agent: AgentMarketPlaceItemWithFilter) => {
    if (agent.supports_file_upload) {
      setIsUploadRequired(true);
      setfile_format_supported(agent.file_format_supported);
    } else {
      setIsUploadRequired(false);
    }
    setAgentDetails(agent);
    setShowChat(true);
  };

  const showCommingSoonChatPage = async (agent: AgentMarketPlaceItemWithFilter) => {
    LogTabEventToGA(`${moreDetailsLinkClickedText}: ${agent.name}`);
    
    // Track the click in analytics
    try {
      const currentUser = accounts[0] || {};
      await trackAgentClick({
        user_id: currentUser.localAccountId || currentUser.homeAccountId || '',
        user_name: currentUser.name || '',
        email: currentUser.username || '',
        agent_id: agent._id,
        agent_name: agent.name,
        click_type: 'more_details',
      });
    } catch (error) {
      console.error('Failed to track agent click:', error);
      // Don't block the UI flow if tracking fails
    }
    
    setShowChat(true);
    setShowCommingSoonChat(true);
    setAgentDetails(agent);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleAgentClick = async (agent: AgentMarketPlaceItemWithFilter) => {
    LogButtonEventToGA(`${agentSendClickedText}: ${agent.name}`);
    const agentStatusResponse = await handleAgentAction(agent._id, agent.custom_ui);
    if (agentStatusResponse === 'STOPPED') {
      setAgentStartedAlready(agent._id, false);
      skipAllAgentsStatusCheck.current = false;
      enqueueSnackbar('This Agent is in stopped State currently.');
      return;
    }
    if (agent.custom_ui === 'internal') {
      // Open the custom UI in a new tab
      window.open(`${baseUrl.baseUrl[0].url}/${agent.service_name}/`, '_blank');
    } else if (agent.custom_ui === 'external') {
      // Open the external UI in a new tab
      window.open(agent.ui_url, '_blank');
    } else if (agent.downloadable_only) {
      showCommingSoonChatPage(agent);
    } else {
      activateAgent(agent);
    }
  };

  const clearAgentFilters = () => {
    dispatch(resetAgentsFilters());
    LogButtonEventToGA(clearFiltersClickedText);
    setPage(1);
  };

  const handleDemoClick = (agent: AgentMarketPlaceItemWithFilter) => {
    setSelectedAgentForDemo(agent);
    putAgentDemoVideoMutation({
      partyId: agent._id,
      payload: {
        demo_video_url: agent.demo_video_url,
      },
    });
  };

  const handleCloseDemoModal = () => {
    setSelectedAgentForDemo(null);
  };

  // Get all selected filter values for chip display
  const getAllSelectedFilters = () => {
    const allSelected = [];
    if (selectedFilters.category.length > 0) {
      allSelected.push(...selectedFilters.category.map((filter) => ({ value: filter, type: 'Domain / Industry' })));
    }
    if (selectedFilters.role.length > 0) {
      allSelected.push(...selectedFilters.role.map((filter) => ({ value: filter, type: 'Role' })));
    }
    if (selectedFilters.cloudProvider.length > 0) {
      allSelected.push(...selectedFilters.cloudProvider.map((filter) => ({ value: filter, type: 'Cloud Provider' })));
    }
    return allSelected;
  };

  const removeFilter = (filterValue: string, filterType: string) => {
    const section = agentCardsFilterSections.find((s) => s.title === filterType);
    if (section) {
      const newFilters = section.selectedFilters.filter((f) => f !== filterValue);
      section.setSelectedFilters(newFilters);
      setPage(1);
    }
  };

  const hasSelectedFilters =
    selectedFilters.category.length > 0 || selectedFilters.role.length > 0 || selectedFilters.cloudProvider.length > 0;

  const handleAgentDownload = async (agent: AgentMarketPlaceItemWithFilter) => {
    if (isAgentDownloadable(agent)) {
      LogButtonEventToGA(`${agentDownloadClickedText}: ${agent.name}`);
      await downloadAgent(
        {
          partyId: agent._id,
          agentName: agent.name,
          custom_ui: agent.custom_ui,
          download_url: agent.download_url,
        },
        downloadAgentMutation
      );
      refetchAgentList();
    }
  };

  //function to check if agent was updated within the last two weeks
  const isAgentNewlyUpdated = (lastUpdated: string): boolean => {
    const updatedDate = new Date(lastUpdated);
    const currentDate = new Date();
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(currentDate.getDate() - 14);
    return updatedDate >= twoWeeksAgo;
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'flex-start',
        }}
      >
        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 2,
            paddingTop: '100px',
            alignItems: 'center',
          }}
          id="why-agent-section"
        >
          {agentListLoading ? (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                my: 10,
              }}
            >
              <CircularProgress />
              <Typography variant="h5">Loading Agents</Typography>
            </Box>
          ) : (
            <>
              {/* Horizontal Filter Bar */}
              <Box className="agent-filter-controls-container">
                <Box className="agent-filter-controls-row">
                  <AgentCardsFilter
                    agentCardsFilterSections={agentCardsFilterSections}
                    agentList={filteredAgentList}
                    agentListLoading={agentListLoading}
                    onPageChange={setPage}
                  />

                  <TextField
                    className="agent-search-field"
                    label={
                      <Box component="span">
                        Search{' '}
                        <Box component="span" sx={{ fontWeight: 'bold', display: 'inline' }}>
                          {filteredAgents.length}
                        </Box>{' '}
                        agents by Name, Provider, Stack
                      </Box>
                    }
                    variant="outlined"
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        LogButtonEventToGA(`${agentSearchPerformedText}: ${searchTerm}`);
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon sx={{ color: 'action.active' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '8px',
                      },
                      '& .MuiInputLabel-root': {
                        transform: 'translate(14px, 13px) scale(1)',
                        maxWidth: 'calc(100% - 45px)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)',
                          maxWidth: 'none',
                        },
                      },
                    }}
                  />

                  <Box className="agent-sort-controls">
                    <FormControl variant="outlined" className="sort-select">
                      <InputLabel id="sort-select-label">Sort By</InputLabel>
                      <Select
                        labelId="sort-select-label"
                        id="sort-select"
                        value={sortAgentsField}
                        onChange={handleAgentsSortFieldChange}
                        label="Sort By"
                        startAdornment={<MuiIcons.Sort sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />}
                        sx={{
                          height: '50px',
                          borderRadius: '8px',
                        }}
                      >
                        <MenuItem value="">None</MenuItem>
                        {Object.entries(SortAgentsField).map(([key, value]) => (
                          <MenuItem key={value} value={value}>
                            {key === 'Category'
                              ? 'Domain / Industry'
                              : key.includes('_')
                                ? key.replace(/_/g, ' ')
                                : key}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={handleToggleAgentsSortOrder}
                      disabled={!sortAgentsField}
                      title={`Sort ${sortAgentFieldsOrder === SortAgentsFieldsOrder.Ascending ? 'Ascending' : 'Descending'}`}
                      sx={{ height: '50px' }}
                    >
                      {sortAgentFieldsOrder === SortAgentsFieldsOrder.Ascending ? (
                        <ArrowUpwardIcon />
                      ) : (
                        <ArrowDownwardIcon />
                      )}
                    </IconButton>
                  </Box>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showOnlyGoldRating}
                        onChange={handleGoldRatingCheckboxChange}
                        sx={{ 
                          color: '#FFD700',
                          '&.Mui-checked': {
                            color: '#FFD700',
                          },
                        }}
                      />
                    }
                    label="Certified Agent"
                    sx={{
                      display: 'flex',
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      },
                    }}
                  />
                </Box>
                {/* Clear All Button and Selected Filter Chips - New Row */}
                {(hasSelectedFilters || getAllSelectedFilters().length > 0) && (
                  <Box className="agent-active-filters-row">
                    {getAllSelectedFilters().length > 0 && (
                      <Box className="selected-filters-chips">
                        {getAllSelectedFilters().map((filter, index) => (
                          <Chip
                            key={`${filter.type}-${filter.value}-${index}`}
                            label={`${filter.value}`}
                            variant="filled"
                            color="primary"
                            size="small"
                            onDelete={() => removeFilter(filter.value, filter.type)}
                            sx={{
                              '& .MuiChip-deleteIcon': {
                                color: 'inherit',
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}

                    {hasSelectedFilters && (
                      <Button
                        className="clear-all-button"
                        color="error"
                        size="large"
                        onClick={clearAgentFilters}
                        sx={{
                          textTransform: 'none',
                          height: '40px',
                          borderRadius: '8px',
                          whiteSpace: 'nowrap',
                          px: 2,
                        }}
                      >
                        Clear All
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                flexWrap="wrap"
                gap={3}
                marginTop={2}
                marginBottom={2}
                width="100%"
                sx={{
                  padding: { xs: '0', lg: '0' },
                  display: 'flex',
                  flexWrap: 'wrap',
                  columnGap: '40px',
                  rowGap: '40px',
                }}
              >
                <Box className="agents-card-grid">
                  {paginatedAgents.map((agent) => {
                    return (
                      <Card
                        key={agent._id}
                        sx={{
                          textAlign: 'left',
                          boxShadow: 2,
                          borderRadius: 2,
                          p: 1,
                          bgcolor: '#fff',
                          height: '418px',
                          display: 'flex',
                          width: { xs: '100%', sm: '240px' },
                          flexDirection: 'column',
                          border: '1px solid #ddd',
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            height: 80,
                            backgroundImage: `url(${backLogo})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            position: 'relative',
                          }}
                        >
                          {(goldList && Array.isArray(goldList) && goldList.some((item) => item.agent_id === agent._id && item.gold_rating)) || isAgentNewlyUpdated(agent.last_updated) ? (
                            <Box
                            >
                              {/* Gold Certificate Badge Icon (PNG) */}
                              {goldList && Array.isArray(goldList) && goldList.some((item) => item.agent_id === agent._id && item.gold_rating) && (
                                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }} title="Gold Certified Agent">
                                  <img
                                    src={goldCertifiedBadge}
                                    alt="Gold Certified Badge"
                                    style={{ width: 50, position: 'absolute', left: 5, top: 0, bottom: 0}}
                                  />
                                </Box>
                              )}
                              {isAgentNewlyUpdated(agent.last_updated) && (
                                <Chip
                                  label="NEW"
                                  size="small"
                                  sx={{
                                    backgroundColor: '#FFD700',
                                    color: '#000',
                                    fontSize: '0.5rem',
                                    height: '18px',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    position: 'absolute',
                                    top: 25,
                                    right: 10,
                                    zIndex: 2,
                                  }}
                                />
                              )}
                            </Box>
                          ) : null}
                        </Box>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: '#f0f0f0',
                            mx: 'auto',
                            mt: -3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 1,
                            zIndex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: '80%',
                              height: 27,
                              backgroundImage:
                                agent.cloud_provider.toLowerCase() === 'amazon'
                                  ? `url(${amazonLogo})`
                                  : agent.cloud_provider.toLowerCase() === 'google'
                                    ? `url(${googleLogo})`
                                    : agent.cloud_provider.toLowerCase() === 'servicenow'
                                      ? `url(${servicenowLogo})`
                                      : agent.cloud_provider.toLowerCase() === 'microsoft' || agent.cloud_provider.toLowerCase() === 'microsoft copilot studio'
                                        ? `url(${microsoftLogo})`
                                        : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                        </Box>

                        <CardContent
                          sx={{
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            px: 1,
                            pt: 1,
                            pb: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Tooltip title={agent.category} placement="top" arrow>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  float: 'left',
                                  backgroundColor: '#f0f0f0',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.25,
                                  color: '#555',
                                  letterSpacing: 0.4,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '40%',
                                }}
                              >
                                {agent.category}
                              </Typography>
                            </Tooltip>
                            <Tooltip title={agent.role} placement="top" arrow>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  float: 'right',
                                  backgroundColor: '#f0f0f0',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.25,
                                  color: '#555',
                                  letterSpacing: 0.4,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '40%',
                                }}
                              >
                                {agent.role}
                              </Typography>
                            </Tooltip>
                          </Box>

                          <Tooltip title={agent.name} placement="top" arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
                             
                              <Typography
                                fontWeight={700}
                                fontSize={15}
                                textAlign="left"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {agent.name}
                              </Typography>
                            </Box>
                          </Tooltip>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <MuiIcons.Description fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Tooltip title={agent.description} placement="top" arrow>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 3,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  minHeight: '60px',
                                }}
                              >
                                {agent.description}
                              </Typography>
                            </Tooltip>
                          </Box>
                          {agent.tech_stack.length && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mt: 0.5,
                              }}
                            >
                              {/* <MuiIcons.Code  /> */}
                              <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                              <Tooltip title={agent.tech_stack.join(', ')} placement="top" arrow>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  textOverflow={'ellipsis'}
                                  whiteSpace={'nowrap'}
                                  overflow={'hidden'}
                                >
                                  {agent.tech_stack.join(', ')}
                                </Typography>
                              </Tooltip>
                            </Box>
                          )}
                          {agent.isLive !== false && (
                            <>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mt: 0.5,
                                }}
                              >
                                <MuiIcons.CalendarToday fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="caption" color="textSecondary">
                                  {agent.last_updated}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mt: 0.5,
                                }}
                              >
                                <MuiIcons.Person fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                {agent.owners.map((owner, index) => (
                                  <Tooltip key={index} title={owner.name} placement="top" arrow>
                                    <Typography
                                      key={index}
                                      variant="caption"
                                      color="textSecondary"
                                      sx={{
                                        display: 'inline',
                                        marginRight: index < agent.owners.length - 1 ? '8px' : '0',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      <Link
                                        href={`mailto:${owner.email}`}
                                        color="textSecondary"
                                        sx={{
                                          cursor: 'pointer',
                                          fontWeight: 'bold',
                                          fontSize: 'inherit',
                                          textDecoration: 'none',
                                          '&:hover': {
                                            textDecoration: 'underline',
                                            color: 'rgb(25, 118, 210)',
                                          },
                                        }}
                                      >
                                        {owner.name}
                                      </Link>
                                    </Typography>
                                  </Tooltip>
                                ))}
                              </Box>
                       
                                {agent.cloud_provider.toLowerCase() === 'google' && agent.agents && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 0.5,
                                  }}
                                >
                                  <MuiIcons.Group fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="textSecondary">
                                    {agent.agents} Sub Agents
                                  </Typography>
                                </Box>
                              )}
                            
                            </>
                          )}

                          {
                            <>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  justifyContent: 'left',
                                  // Reduced the top margin to match the isLive=true layout
                                  mt: 0,
                                  mb: 'auto',
                                }}
                              >
                                <Box
                                  sx={{
                                    mt: 1,
                                    display: 'flex',
                                    width: '100%',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        textDecoration: 'underline',
                                        color: '#1976d2',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                      }}
                                      onClick={() => showCommingSoonChatPage(agent)}
                                    >
                                      More Details
                                    </Typography>
                                    {agent.more_details_count !== undefined && agent.more_details_count > 0 && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: 'text.secondary',
                                          fontSize: '0.75rem',
                                          fontWeight: 600,
                                        }}
                                      >
                                        ({agent.more_details_count})
                                      </Typography>
                                    )}
                                  </Box>

                                  {agent.demo_video_url && (
                                    <PlayCircleOutlineIcon
                                      onClick={() => handleDemoClick(agent)}
                                      sx={{
                                        color: '#1976d2',
                                        cursor: 'pointer',
                                        ml: '.25rem',
                                      }}
                                    />
                                  )}
                                </Box>
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 1, cursor: 'pointer' }}
                                  onClick={() => setSelectedAgentForReview(agent)}
                                >
                                  <MuiIcons.Star fontSize="small" sx={{ color: '#FFD700', mr: 0.5 }} />
                                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, mr: 0.5 }}>
                                    {agent.average_rating ? agent.average_rating.toFixed(1) : '0.0'}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
                                    ({agent.ratings_count ?? 0})
                                  </Typography>
                                </Box>
                              </Box>
                            </>
                          }
                        </CardContent>
                        <Box
                          sx={{
                            mt: 'auto',
                            pb: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            px: 1,
                            alignItems: 'center',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title={`Download ${agent.name}`} arrow placement="top">
                              <Box
                                sx={{
                                  opacity: isAgentDownloadable(agent) ? 1 : 0.5,
                                  pointerEvents: isAgentDownloadable(agent) ? 'auto' : 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <DownloadIcon
                                  onClick={() => handleAgentDownload(agent)}
                                  sx={{
                                    color: '#1976d2',
                                    cursor: isAgentDownloadable(agent) ? 'pointer' : 'default',
                                  }}
                                />
                              </Box>
                            </Tooltip>
                            <Tooltip title="Downloaded count" arrow placement="top">
                              <Typography
                                sx={{ fontWeight: 'bold', marginBottom: '5px', cursor: 'pointer', fontSize: '0.75rem' }}
                              >
                                {agent.download_count}
                              </Typography>
                            </Tooltip>
                          </Box>

                          <Box
                            sx={{
                              opacity: isAgentChattable(agent) ? 1 : 0.5,
                              pointerEvents: isAgentChattable(agent) ? 'auto' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            {getAgentsStatus(agent._id) === 'STOPPED' ? (
                              <>
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    padding: '4px 8px',
                                    minWidth: 'auto',
                                    '&:hover': {
                                      backgroundColor: '#1565c0',
                                    },
                                    '&:disabled': {
                                      backgroundColor: '#ccc',
                                      color: '#666',
                                    },
                                  }}
                                  disabled={agent.isLive === false || agent.downloadable_only === true}
                                  onClick={async () => {
                                    // Track Start Agent click
                                    try {
                                      const currentUser = accounts[0] || {};
                                      await trackAgentClick({
                                        user_id: currentUser.localAccountId || currentUser.homeAccountId || '',
                                        user_name: currentUser.name || '',
                                        email: currentUser.username || '',
                                        agent_id: agent._id,
                                        agent_name: agent.name,
                                        click_type: 'start_agent',
                                      });
                                    } catch (error) {
                                      console.error('Failed to track agent click:', error);
                                    }
                                    handleAgentAction(agent._id, agent.custom_ui);
                                  }}
                                >
                                  Start Agent
                                </Button>
                                <Tooltip title="Start count" arrow placement="top">
                                  <Typography
                                    sx={{
                                      fontWeight: 'bold',
                                      marginBottom: '5px',
                                      cursor: 'pointer',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    ({agent.start_agent_count !== undefined ? agent.start_agent_count : 0})
                                  </Typography>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title={`Chat with ${agent.name}`} arrow placement="top">
                                  <SendRoundedIcon
                                    sx={{
                                      color: '#1976d2',
                                      cursor:
                                        agent.isLive === false || agent.downloadable_only === true
                                          ? 'default'
                                          : 'pointer',
                                    }}
                                    onClick={async () => {
                                      // Track open chat click
                                      try {
                                        const currentUser = accounts[0] || {};
                                        await trackAgentClick({
                                          user_id: currentUser.localAccountId || currentUser.homeAccountId || '',
                                          user_name: currentUser.name || '',
                                          email: currentUser.username || '',
                                          agent_id: agent._id,
                                          agent_name: agent.name,
                                          click_type: 'open_chat',
                                        });
                                      } catch (error) {
                                        console.error('Failed to track agent click:', error);
                                      }
                                      handleAgentClick(agent);
                                    }}
                                  />
                                </Tooltip>
                                {agent.open_chat_count !== undefined && agent.open_chat_count > 0 && (
                                  <Tooltip title="Chat opened count" arrow placement="top">
                                    <Typography
                                      sx={{
                                        fontWeight: 'bold',
                                        marginBottom: '5px',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      ({agent.open_chat_count})
                                    </Typography>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </Card>
                    );
                  })}
                </Box>
              </Stack>

              {/* Pagination Component */}
              {filteredAgents.length > 0 && (
                <Box
                  sx={{
                    mt: 4,
                    mb: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <Pagination
                    count={totalPageCountOfAgents}
                    page={page}
                    onChange={(event, value) => handleAgentsPageChange(value)}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                  />
                  <Typography variant="caption" sx={{ mt: 1 }} fontSize={'0.75rem'}>
                    Showing {(page - 1) * agentsCardsPerPage + 1}-
                    {Math.min(page * agentsCardsPerPage, filteredAgents.length)} of {filteredAgents.length}
                  </Typography>
                </Box>
              )}

              {filteredAgents.length === 0 && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No agents found matching your criteria
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      {selectedAgentForReview && (
        <AgentReviewAnalysisDialog
          open={!!selectedAgentForReview}
          onClose={() => setSelectedAgentForReview(null)}
          agent={selectedAgentForReview}
          onReviewChange={refetchAgentList}
        />
      )}
      
      {selectedAgentForDemo && <AgentDemoVideoDialog onClose={handleCloseDemoModal} agent={selectedAgentForDemo} />}

      {/* Chatbot Component */}
      <AgentFinderChatBot />
    </Box>
  );
}

export default AgentMarketPlace;
